import * as React from "react";
import { clsx } from "clsx";
import { AnalyticsTrigger } from "../../../helpers/analytics/analytics-trigger/analytics-trigger.mjs";
import { Card } from "../../../card/card/card.mjs";
import "debounce";
import "tabbable";
import { usePrevious } from "../../../helpers/helper/hooks/use-previous.mjs";
import { Icon } from "../../../icon/icon/icon.mjs";
import { PropsChecker } from "../../../helpers/props-checker/props-checker.mjs";
const ANIMATION_WAVE_DELAY = 100;
const ANIMATION_IN_DURATION = 300;
const ANIMATION_OUT_DURATION = 400;
const OptionCard = (props) => {
  return /* @__PURE__ */ React.createElement(
    PropsChecker,
    {
      componentName: "OptionCard",
      componentProps: props,
      defaultValueName: "defaultChecked",
      valueName: "checked",
      onChangeNames: "onChange",
      isComponentDisabled: props.disabled
    },
    /* @__PURE__ */ React.createElement(
      AnalyticsTrigger,
      {
        trigger: {
          callback: "onChange",
          id: ([checked]) => "Option - ".concat(checked ? "Check" : "Uncheck", " - ").concat(props.id),
          elementId: props.id,
          elementType: "option",
          interactionType: ([checked]) => "".concat(checked ? "check" : "uncheck")
        }
      },
      /* @__PURE__ */ React.createElement(OptionCardInner, { ...props })
    )
  );
};
const OptionCardInner = (props) => {
  const {
    background,
    borderRadius,
    className,
    checked,
    children,
    defaultChecked,
    disabled,
    elevation,
    id,
    index,
    input,
    inputType,
    isInvalid,
    labelText,
    padding,
    responsive,
    style,
    type,
    value = "on"
  } = props;
  const [animationActive, setAnimationActive] = React.useState(false);
  const [animationDirection, setAnimationDirection] = React.useState("in");
  const [isCardChecked, setIsCardChecked] = React.useState(defaultChecked || false);
  const [directionTimeout, setDirectionTimeout] = React.useState(0);
  const onClick = () => onChange();
  const isChecked = props.onChange && typeof checked !== "undefined" ? checked : isCardChecked;
  const inputRef = React.useRef(null);
  let waveTimeout = 0;
  const prevIsInvalid = usePrevious(isInvalid);
  React.useEffect(() => {
    if (!prevIsInvalid && isInvalid) {
      startWaveAnimation();
    } else if (prevIsInvalid && !isInvalid) {
      endAnimation();
      clearTimeouts();
    }
    return () => {
      clearTimeouts();
    };
  });
  const clearTimeouts = () => {
    if (directionTimeout) {
      window.clearTimeout(directionTimeout);
    }
    if (waveTimeout) {
      window.clearTimeout(waveTimeout);
    }
  };
  const getNextChecked = (event) => {
    if (inputType === "radio") {
      return true;
    }
    if (event) {
      return event.currentTarget.checked;
    }
    if (inputRef.current) {
      return !inputRef.current.checked;
    }
    return !isChecked;
  };
  const onChange = (event) => {
    const nextChecked = getNextChecked(event);
    if (disabled || inputType === "radio" && isChecked === nextChecked) {
      return;
    }
    if (typeof checked === "undefined") {
      setIsCardChecked(nextChecked);
    }
    if (props.onChange) {
      props.onChange(nextChecked);
    }
  };
  const startWaveAnimation = () => {
    if (isInvalid && typeof index !== "undefined") {
      waveTimeout = window.setTimeout(() => {
        startAnimation();
      }, index * ANIMATION_WAVE_DELAY);
    }
  };
  const startAnimation = () => {
    setAnimationActive(true);
    setAnimationDirection("in");
    triggerFadeIn();
  };
  const endAnimation = () => {
    setAnimationActive(false);
    setAnimationDirection("idle");
  };
  const triggerFadeIn = () => {
    setDirectionTimeout(
      window.setTimeout(() => {
        setAnimationDirection("out");
        triggerFadeOut();
      }, ANIMATION_IN_DURATION)
    );
  };
  const triggerFadeOut = () => {
    setDirectionTimeout(
      window.setTimeout(() => {
        endAnimation();
      }, ANIMATION_OUT_DURATION)
    );
  };
  const renderIndicator = () => {
    if (!isCardChecked && disabled) {
      return;
    }
    return /* @__PURE__ */ React.createElement("span", { className: "option-card__indicator" }, inputType === "checkbox" && /* @__PURE__ */ React.createElement(Icon, { modifiers: "#check", className: "option-card__indicator-icon" }));
  };
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: clsx(
        "option-card",
        "option-card--input-".concat(inputType),
        {
          ["option-card--".concat(type)]: !!type,
          ["option-card--error"]: !!isInvalid,
          ["is-animating"]: !!animationActive,
          ["option-card--animate-".concat(animationDirection)]: !!animationDirection && isInvalid,
          ["is-checked"]: !!isChecked
        },
        className
      ),
      onClick
    },
    /* @__PURE__ */ React.createElement(
      "input",
      {
        ...input,
        value,
        type: inputType,
        ref: inputRef,
        onChange,
        onClick: (event) => event.stopPropagation(),
        checked: isChecked,
        defaultChecked: void 0,
        disabled,
        id,
        className: "option-card__input",
        "aria-invalid": isInvalid
      }
    ),
    /* @__PURE__ */ React.createElement("label", { htmlFor: id, className: "option-card__label" }, labelText),
    /* @__PURE__ */ React.createElement(
      Card,
      {
        className: "option-card__body",
        responsive,
        padding,
        borderRadius,
        style,
        elevation: disabled ? void 0 : elevation,
        background: disabled ? "transparent" : background,
        type
      },
      /* @__PURE__ */ React.createElement("div", { className: "option-card__elements" }, renderIndicator(), children)
    )
  );
};
export {
  OptionCard
};
